import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ClipboardList, Plus, AlertTriangle, CheckCircle2, PauseCircle, MessageSquareQuote } from 'lucide-react'
import api from '@/lib/api'

type Rdc = {
  id: string; code: string; status: string
  tipo_comercial: string; tipo_tecnico: string | null
  descricao_cliente: string
  pode_ofertar: boolean; bloqueio_oferta: string | null
  frase_para_cliente: string; suspenso: boolean
  prazo_agenda_em: string | null; prazo_conclusivo_em: string | null
  prazo_preliminar_em: string | null; prazo_financeiro_em: string | null
}

const ESTADOS: Record<string, { label: string; cls: string }> = {
  coleta: { label: 'Em coleta', cls: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  registrado: { label: 'Aguardando agenda', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  passagem_agendada: { label: 'Passagem agendada', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  acordo_pendente: { label: 'De acordo pendente', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  de_acordo: { label: 'De acordo', cls: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300' },
  parecer_preliminar: { label: 'Parecer preliminar', cls: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300' },
  parecer_conclusivo: { label: 'Parecer conclusivo', cls: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300' },
  financeiro_pendente: { label: 'Aguardando Financeiro', cls: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300' },
  habilitado: { label: 'Habilitado para oferta', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
  deliberacao: { label: 'Em deliberação', cls: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300' },
  encerrado: { label: 'Encerrado', cls: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400' },
}

const TIPOS: Record<string, string> = {
  A: 'A · Escopo padrão', B: 'B · Não confirmada', C: 'C · Nova funcionalidade',
  D: 'D · Projeto aderente', E: 'E · Sem aderência',
}

// Qual obrigacao esta na vez. E o campo mais consultado da tela: o vendedor vem
// aqui para saber o que esperar e de quem, nao para ler o parecer.
function proximaObrigacao(r: Rdc): { rotulo: string; prazo: string | null } {
  switch (r.status) {
    case 'coleta': return { rotulo: 'Concluir a coleta e registrar', prazo: null }
    case 'registrado': return { rotulo: 'Agenda de passagem, com a Tecnologia', prazo: r.prazo_agenda_em }
    case 'passagem_agendada': return { rotulo: 'Realizar a passagem, com gravação', prazo: r.prazo_agenda_em }
    case 'acordo_pendente': return { rotulo: '"De acordo" das duas partes', prazo: null }
    case 'de_acordo': return { rotulo: 'Parecer preliminar, com a Tecnologia', prazo: r.prazo_preliminar_em }
    case 'parecer_preliminar': return { rotulo: 'Parecer conclusivo, com a Tecnologia', prazo: r.prazo_conclusivo_em }
    case 'financeiro_pendente': return { rotulo: 'Manifestação do Financeiro', prazo: r.prazo_financeiro_em }
    case 'deliberacao': return { rotulo: 'Deliberação do Diretor Presidente', prazo: null }
    case 'habilitado': return { rotulo: 'Liberado — pode ofertar o que o parecer confirma', prazo: null }
    default: return { rotulo: '—', prazo: null }
  }
}

const dt = (v: string | null) =>
  v ? new Date(v).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'

export default function RdcPanel({ opportunityId }: { opportunityId: string }) {
  const nav = useNavigate()
  const [itens, setItens] = useState<Rdc[]>([])
  const [carregando, setCarregando] = useState(true)

  const carregar = useCallback(() => {
    api.get(`/v1/rdc/por-oportunidade/${opportunityId}`)
      .then(({ data }) => setItens(data.items || []))
      .catch(() => setItens([]))
      .finally(() => setCarregando(false))
  }, [opportunityId])

  useEffect(() => { carregar() }, [carregar])

  return (
    <div className="px-6 py-4 border-t">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <ClipboardList className="h-5 w-5" /> Registro de Demanda Comercial
        </h2>
        <button
          onClick={() => nav(`/rdc/novo?oportunidade=${opportunityId}`)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm
                     bg-primary text-primary-foreground hover:opacity-90 transition"
        >
          <Plus className="h-4 w-4" /> Novo RDC
        </button>
      </div>

      {carregando && <div className="text-sm text-muted-foreground">Carregando…</div>}

      {!carregando && itens.length === 0 && (
        <div className="rounded-lg border bg-muted/20 p-4 text-sm text-muted-foreground">
          Nenhuma demanda registrada nesta oportunidade. Identificou necessidade na reunião?
          Registre em até 1 dia útil — demanda não registrada não existe para efeito do
          procedimento, e o time técnico não é cobrável por ela.
        </div>
      )}

      <div className="space-y-3">
        {itens.map((r) => {
          const e = ESTADOS[r.status] || { label: r.status, cls: 'bg-slate-100 text-slate-700' }
          const prox = proximaObrigacao(r)
          return (
            <div key={r.id} onClick={() => nav(`/rdc/${r.id}`)}
                 className="rounded-lg border bg-card p-4 space-y-3 cursor-pointer
                            hover:border-primary/40 transition">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{r.code}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${e.cls}`}>{e.label}</span>
                  {r.suspenso && (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                      <PauseCircle className="h-3.5 w-3.5" /> prazo suspenso
                    </span>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">
                  {TIPOS[r.tipo_tecnico || r.tipo_comercial]}
                  {r.tipo_tecnico && r.tipo_tecnico !== r.tipo_comercial && (
                    <span className="text-amber-600"> · reclassificado de {r.tipo_comercial}</span>
                  )}
                </span>
              </div>

              <p className="text-sm text-muted-foreground line-clamp-3">{r.descricao_cliente}</p>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-md bg-muted/40 p-3">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
                    Próxima obrigação
                  </div>
                  <div className="text-sm">{prox.rotulo}</div>
                  {prox.prazo && (
                    <div className="text-xs text-muted-foreground mt-0.5 tabular-nums">
                      vence em {dt(prox.prazo)}
                    </div>
                  )}
                </div>
                <div className="rounded-md bg-muted/40 p-3">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
                    Pode ofertar?
                  </div>
                  {r.pode_ofertar ? (
                    <div className="text-sm text-emerald-600 flex items-start gap-1.5">
                      <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                      Sim, no que o parecer confirma
                    </div>
                  ) : (
                    <div className="text-sm flex items-start gap-1.5">
                      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
                      <span>{r.bloqueio_oferta}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-md border-l-2 border-sky-500 bg-sky-50/50 dark:bg-sky-950/20 p-3">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1
                                flex items-center gap-1.5">
                  <MessageSquareQuote className="h-3.5 w-3.5" /> O que dizer ao cliente hoje
                </div>
                <div className="text-sm italic">{r.frase_para_cliente}</div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
